import { Injectable, inject, PLATFORM_ID } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { isPlatformServer } from '@angular/common';
import { Observable, Subject, BehaviorSubject } from 'rxjs';
import { io, Socket } from 'socket.io-client';

export interface TextLayer {
  id: string;
  text: string;
  imageUrl?: string;
  width?: number;
  height?: number;
  x: number;
  y: number;
  fontSize: number;
  fontFamily: string;
  color: string;
  fontWeight: string;
  fontStyle: string;
  effect: string;
}

export interface GalleryArea {
  top: number;
  left: number;
  width: number;
  height: number;
}

export interface FrameArea {
  top: number;
  left: number;
  width: number;
  height: number;
}

export interface AppEvent {
  id: string;
  slug?: string | null;
  title: string;
  frameUrl: string | null;
  frameArea?: FrameArea;
  overlayText: string; 
  overlayPosition: string;
  textLayers?: TextLayer[];
  primaryColor: string;
  font: string;
  galleryBackgroundUrl?: string;
  kioskBackgroundUrl?: string;
  galleryBackgroundVideoMuted?: boolean;
  kioskBackgroundVideoMuted?: boolean;
  galleryYoutubeVideoId?: string;
  kioskYoutubeVideoId?: string;
  requireModeration?: boolean;
  galleryArea?: GalleryArea;
  logoScale?: number;
  logoPosition?: { top: number; left: number };
  kioskLogoScale?: number;
  galleryLayoutMode?: 'floating' | 'center-title' | 'shape' | 'carousel' | 'grid' | 'infinite-wall';
  galleryShapeText?: string;
  showGalleryTitle?: boolean;
  titleImageUrl?: string;
  additionalImageUrl?: string;
  messagePrompt?: string;
  whatsappEnabled?: boolean;
  whatsappToken?: string;
  whatsappPhoneId?: string;
  whatsappBusinessId?: string;
  whatsappMessageTemplate?: string;
  collectLeadData?: boolean;
  leadDataConfig?: Record<string, unknown>;
  createdAt: string;
}

export interface PhotoEntry {
  id: string;
  eventId: string;
  imageUrl: string;
  guestName: string;
  whatsappNumber?: string;
  message: string;
  status?: 'pending' | 'approved' | 'rejected';
  source?: 'selfie' | 'kiosk' | 'unknown';
  createdAt: string;
}

export interface PhotoCounts {
  pending: number;
  approved: number;
  rejected: number;
}

export interface EventAnalytics {
  eventId: string;
  totalPhotos: number;
  kioskPhotos: number;
  selfiePhotos: number;
  selfieOpens: number;
  selfieUniqueUsers: number;
  downloads: number;
  shares: number;
}

export interface AdminAccessResult {
  mode: 'superadmin' | 'event';
  events: AppEvent[];
}

@Injectable({
  providedIn: 'root'
})
export class Events {
  private http = inject(HttpClient);
  private platformId = inject(PLATFORM_ID);
  private socket: Socket | null = null;
  private joinedEvents = new Set<string>();
  private joinedAdminEvents = new Map<string, string>();

  // Real-time events
  private eventUpdatedSubject = new Subject<AppEvent>();
  private newPhotoSubject = new Subject<PhotoEntry>();
  private photoRemovedSubject = new Subject<string>();
  private photoStatusChangedSubject = new Subject<PhotoEntry>();
  private newPhotoAdminSubject = new Subject<PhotoEntry>();
  private analyticsUpdatedSubject = new Subject<EventAnalytics>();
  private connectionStatusSubject = new BehaviorSubject<boolean>(false);

  eventUpdated$ = this.eventUpdatedSubject.asObservable();
  newPhoto$ = this.newPhotoSubject.asObservable();
  photoRemoved$ = this.photoRemovedSubject.asObservable();
  photoStatusChanged$ = this.photoStatusChangedSubject.asObservable();
  newPhotoAdmin$ = this.newPhotoAdminSubject.asObservable();
  analyticsUpdated$ = this.analyticsUpdatedSubject.asObservable();
  connectionStatus$ = this.connectionStatusSubject.asObservable();
  
  constructor() {}

  joinEvent(eventId: string, isAdmin = false, pin = '') {
    const socket = this.ensureSocket();
    if (!socket) {
      console.warn('Socket not initialized, cannot join event');
      return;
    }

    console.log(`Joining event room: ${eventId}${isAdmin ? ' (admin)' : ''}`);
    if (isAdmin) {
      this.joinedAdminEvents.set(eventId, pin);
      socket.emit('join_admin_event', { eventId, pin });
    } else {
      this.joinedEvents.add(eventId);
      socket.emit('join_event', eventId);
    }
  }

  private ensureSocket(): Socket | null {
    if (isPlatformServer(this.platformId)) return null;
    if (this.socket) return this.socket;

    this.socket = io({
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 45000,
      transports: ['websocket', 'polling'],
      upgrade: true,
      path: '/socket.io/'
    });

    this.socket.on('connect', () => {
      console.log('Socket connected with transport:', this.socket?.io.engine.transport.name);
      this.connectionStatusSubject.next(true);
      this.rejoinRooms();
    });
    this.socket.on('disconnect', () => {
      console.log('Socket disconnected');
      this.connectionStatusSubject.next(false);
    });
    this.socket.on('connect_error', (error) => {
      console.error('Socket connection error:', error);
      this.connectionStatusSubject.next(false);
    });

    this.socket.on('event_updated', (event: AppEvent) => this.eventUpdatedSubject.next(event));
    this.socket.on('new_photo', (photo: PhotoEntry) => this.newPhotoSubject.next(photo));
    this.socket.on('photo_removed', (photoId: string) => this.photoRemovedSubject.next(photoId));
    this.socket.on('photo_status_changed', (photo: PhotoEntry) => this.photoStatusChangedSubject.next(photo));
    this.socket.on('new_photo_admin', (photo: PhotoEntry) => this.newPhotoAdminSubject.next(photo));
    this.socket.on('analytics_updated', (analytics: EventAnalytics) => this.analyticsUpdatedSubject.next(analytics));

    return this.socket;
  }

  private rejoinRooms() {
    if (!this.socket) return;
    this.joinedEvents.forEach(eventId => this.socket?.emit('join_event', eventId));
    this.joinedAdminEvents.forEach((pin, eventId) => this.socket?.emit('join_admin_event', { eventId, pin }));
  }
  
  private get baseUrl(): string {
    return isPlatformServer(this.platformId) ? 'http://localhost:3000' : '';
  }

  getEvents(adminPin?: string): Observable<AppEvent[]> {
    const options = adminPin ? { headers: new HttpHeaders({ 'x-admin-pin': adminPin }) } : {};
    return this.http.get<AppEvent[]>(`${this.baseUrl}/api/events`, options);
  }

  adminAccess(pin: string): Observable<AdminAccessResult> {
    return this.http.post<AdminAccessResult>(`${this.baseUrl}/api/admin/access`, { pin });
  }

  getEvent(id: string): Observable<AppEvent> {
    return this.http.get<AppEvent>(`${this.baseUrl}/api/events/${id}`);
  }

  createEvent(data: Partial<AppEvent> & { editPin?: string; _adminPin?: string }): Observable<AppEvent> {
    const adminPin = data._adminPin || (!isPlatformServer(this.platformId) ? sessionStorage.getItem('studiobooth:admin-pin') || '' : '');
    return this.http.post<AppEvent>(`${this.baseUrl}/api/events`, { ...data, _adminPin: adminPin });
  }

  updateEvent(id: string, data: Partial<AppEvent> & { _editPin?: string }): Observable<AppEvent> {
    return this.http.put<AppEvent>(`${this.baseUrl}/api/events/${id}`, data);
  }

  verifyEventPin(id: string, pin: string): Observable<{ success: boolean }> {
    return this.http.post<{ success: boolean }>(`${this.baseUrl}/api/events/${id}/verify-pin`, { pin });
  }

  resetEventPin(id: string, superAdminPin: string, newPin: string): Observable<{ success: boolean }> {
    return this.http.post<{ success: boolean }>(`${this.baseUrl}/api/events/${id}/reset-pin`, { superAdminPin, newPin });
  }

  deleteEvent(id: string, pin: string): Observable<{ success: boolean; id: string }> {
    return this.http.delete<{ success: boolean; id: string }>(`${this.baseUrl}/api/events/${id}`, {
      body: { pin },
      headers: new HttpHeaders({ 'x-admin-pin': pin })
    });
  }

  uploadFrame(file: File, purpose: 'frame' | 'background' | 'brand' | 'asset' = 'asset'): Observable<{url: string}> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('purpose', purpose);
    return this.http.post<{url: string}>(`${this.baseUrl}/api/upload-frame`, formData);
  }

  getEventPhotos(eventId: string, admin = false, options: { limit?: number; offset?: number; status?: PhotoEntry['status']; pin?: string } = {}): Observable<PhotoEntry[]> {
    const params = new URLSearchParams();
    if (admin) params.set('admin', 'true');
    if (options.limit !== undefined) params.set('limit', String(options.limit));
    if (options.offset !== undefined) params.set('offset', String(options.offset));
    if (options.status) params.set('status', options.status);
    const query = params.toString();
    const requestOptions = options.pin ? { headers: new HttpHeaders({ 'x-event-pin': options.pin }) } : {};
    return this.http.get<PhotoEntry[]>(`${this.baseUrl}/api/events/${eventId}/photos${query ? `?${query}` : ''}`, requestOptions);
  }

  getEventPhotoCounts(eventId: string): Observable<PhotoCounts> {
    return this.http.get<PhotoCounts>(`${this.baseUrl}/api/events/${eventId}/photo-counts`);
  }

  getEventAnalytics(eventId: string, pin?: string): Observable<EventAnalytics> {
    const options = pin ? { headers: new HttpHeaders({ 'x-event-pin': pin }) } : {};
    return this.http.get<EventAnalytics>(`${this.baseUrl}/api/events/${eventId}/analytics`, options);
  }

  trackAnalytics(eventId: string, data: { type: 'selfie_open' | 'photo_downloaded' | 'photo_shared'; source?: string; photoId?: string; sessionId?: string }): Observable<{ success: boolean; analytics: EventAnalytics }> {
    return this.http.post<{ success: boolean; analytics: EventAnalytics }>(`${this.baseUrl}/api/events/${eventId}/analytics`, data);
  }

  getPhoto(photoId: string): Observable<PhotoEntry> {
    return this.http.get<PhotoEntry>(`${this.baseUrl}/api/photos/${photoId}`);
  }

  uploadPhoto(data: { eventId: string, base64Image: string, guestName?: string, whatsappNumber?: string, message?: string, source?: 'selfie' | 'kiosk' | 'unknown' }): Observable<PhotoEntry> {
    if (!isPlatformServer(this.platformId) && typeof FormData !== 'undefined') {
      const imageBlob = this.dataUrlToBlob(data.base64Image);
      if (imageBlob) {
        const form = new FormData();
        form.append('eventId', data.eventId);
        form.append('image', imageBlob, 'photo.jpg');
        if (data.guestName !== undefined) form.append('guestName', data.guestName);
        if (data.whatsappNumber !== undefined) form.append('whatsappNumber', data.whatsappNumber);
        if (data.message !== undefined) form.append('message', data.message);
        if (data.source !== undefined) form.append('source', data.source);
        return this.http.post<PhotoEntry>(`${this.baseUrl}/api/photos`, form);
      }
    }
    return this.http.post<PhotoEntry>(`${this.baseUrl}/api/photos`, data);
  }

  private dataUrlToBlob(dataUrl: string): Blob | null {
    const match = dataUrl.match(/^data:(image\/[A-Za-z0-9.+-]+);base64,(.+)$/);
    if (!match) return null;
    const mimeType = match[1];
    const binary = atob(match[2]);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return new Blob([bytes], { type: mimeType });
  }

  updatePhotoStatus(photoId: string, status: 'approved' | 'rejected', pin?: string): Observable<PhotoEntry> {
    return this.http.put<PhotoEntry>(`${this.baseUrl}/api/photos/${photoId}/status`, { status, pin });
  }

  deletePhoto(photoId: string, pin?: string): Observable<{ success: boolean; id: string }> {
    return this.http.delete<{ success: boolean; id: string }>(`${this.baseUrl}/api/photos/${photoId}`, { body: { pin } });
  }

  sendWhatsApp(eventId: string, phone: string, photoUrl: string): Observable<{ success: boolean }> {
    return this.http.post<{ success: boolean }>(`${this.baseUrl}/api/events/${eventId}/send-whatsapp`, { phone, photoUrl });
  }
}
